import { useCallback, useEffect, useRef, useState } from 'react';

export const HOVER_CARD_OPEN_DELAY_MS = 600;
export const HOVER_CARD_CLOSE_DELAY_MS = 400;

interface UseDelayedHoverPopperOptions {
  openDelayMs?: number;
  closeDelayMs?: number;
}

export function useDelayedHoverPopper(options: UseDelayedHoverPopperOptions = {}) {
  const openDelayMs = options.openDelayMs ?? HOVER_CARD_OPEN_DELAY_MS;
  const closeDelayMs = options.closeDelayMs ?? HOVER_CARD_CLOSE_DELAY_MS;
  const openTimerRef = useRef<number | undefined>(undefined);
  const closeTimerRef = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = undefined;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  }, []);

  const scheduleOpen = useCallback(() => {
    clearCloseTimer();
    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => setOpen(true), openDelayMs);
  }, [clearCloseTimer, clearOpenTimer, openDelayMs]);

  const scheduleClose = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), closeDelayMs);
  }, [clearCloseTimer, clearOpenTimer, closeDelayMs]);

  const openNow = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer, clearOpenTimer]);

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [clearCloseTimer, clearOpenTimer]
  );

  return { open, scheduleOpen, scheduleClose, openNow };
}
